import React, { Fragment } from "react"
import { graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import style from "./product-card.mod.scss"
import {
  Recommendation,
  Price,
  BundleSavings,
  Button,
  Superscript,
  SaleBadge
} from "@components"
import LogoSvg from "@svgs/logo.svg"
import { ConditionalLink } from "@helpers/miscHelpers"
import { getPricing } from "@context/productContext"
import SoldSvg from "@svgs/sold.svg"
import Badge3dSvg from "@svgs/badge-3d.svg"
import Clearlift from "@svgs/clearlift-small.svg"
import UvFree from "@svgs/uv-free.svg"
import UvUpgrade from "@svgs/uv-upgrade.svg"
import FreeService from "@svgs/service.svg"
import FreeHeatPump from "@svgs/free_heat_pump.svg"
import FreeDelivery from "@svgs/free-delivery.svg"
import FastShipping from "@svgs/fast-shipping.svg"
import MotorisedLouvres from "@svgs/motorised-louvres.svg"
import Service from "@svgs/service.svg"
import Combined from "@svgs/combined.svg"
import Infrared from "@svgs/infrared.svg"
import { FaAngleRight } from "@react-icons/all-files/fa/FaAngleRight"
import Finance from "../components/finance"

const formatFreeHeatPumpSavings = value => {
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return Math.floor(n).toLocaleString("en-AU")
}

const ProductCard = ({
  id,
  heading,
  image,
  brand,
  description,
  pricing,
  pricingSource = null,
  finance,
  netoVariants = [],
  dimensions = [],
  onClick,
  links = [],
  link,
  modifiers = [],
  elevated = false,
  padImage,
  hover,
  viewer,
  hoverImage,
  onSale = false,
  imageDisclaimer = false,
  extended = false,
  isAvailable = true,
  special = false,
  details = [],
  showRecommendation = true,
  isSearchResult = false,
  clearliftCompatible = false,
  uvFree = false,
  uvIncluded = false,
  freeService = false,
  freeHeatPump = false,
  freeHeatPumpSavings,
  bundlePriceSavings,
  infrared = false,
  freeDelivery = false,
  motorised = false,
  clearancePscOffer,
  clearanceNote,
  category
}) => {
  if (link) links.push(link)
  const computedPricing = pricingSource ? getPricing(pricingSource) : null
  const resolvedPricing = computedPricing || pricing
  const resolvedOnSale =
    computedPricing?.onSale ?? resolvedPricing?.onSale ?? onSale

  extended && modifiers.push(style["product-card--extended"])
  elevated && modifiers.push(style["product-card--elevated"])
  hover && modifiers.push(style["product-card--hover"])
  hoverImage && modifiers.push(style["product-card--has-hover-image"])
  padImage && modifiers.push(style["product-card--pad-image"])
  special && modifiers.push(style["product-card--special"])

  // const isClearance = category === "Clearance"
  // const isSales = category === "Specials"

  const productCardConfig = {
    className: [style["product-card"], ...modifiers].join(" ")
  }
  const hasLinks = links.length > 0

  const primaryLinkConfig = hasLinks
    ? {
        onClick,
        ...links[0],
        to: links[0]?.to || links[0]?.path
      }
    : {}

  const normalizedClearanceNote = clearanceNote
    ?.replace(/\s+/g, " ") // replace any whitespace (including non-breaking) with normal space
    .trim() // remove leading/trailing whitespace
    .normalize() // normalize unicode
  const hasRunoutSalesIcon = normalizedClearanceNote === "New Model Year Runout"

  const iconComponents = [
    { condition: freeService, Component: FreeService },
    { condition: clearliftCompatible, Component: Clearlift },
    { condition: freeDelivery, Component: FreeDelivery },
    { condition: motorised, Component: MotorisedLouvres },
    { condition: infrared, Component: Infrared },
    { condition: uvFree, Component: UvFree },
    { condition: uvIncluded, Component: UvUpgrade },
    { condition: freeHeatPump, Component: FreeHeatPump },
    {
      condition: normalizedClearanceNote === "Certified Second",
      // Component: SecondIcon
      Component: SecondIcon
    },
    {
      condition: normalizedClearanceNote === "Certified Display Model",
      // Component: DisplayIcon
      Component: DisplayIcon
    },
    {
      condition: normalizedClearanceNote === "Model Runout",
      // Component: RunoutIcon
      Component: RunoutIcon
    },
    {
      condition: normalizedClearanceNote === "Certified Pre-owned",
      // Component: PreownedIcon
      Component: PreownedIcon
    },
    {
      condition: normalizedClearanceNote === "New Model Year Runout",
      // Component: PreownedIcon
      Component: SalesIcon
    }
  ]

  const hasAnyIcon = iconComponents.some(icon => icon.condition)
  const showPrimarySaleBadge = resolvedOnSale && !special && !hasRunoutSalesIcon
  const showPscOfferBadge = clearancePscOffer === "y" && !special

  const hasFreeHeatPumpSavingsValue =
    freeHeatPumpSavings != null &&
    freeHeatPumpSavings !== "" &&
    Number.isFinite(Number(freeHeatPumpSavings))
  const showFreeHeatPumpSavingsBar =
    !isSearchResult && freeHeatPump && hasFreeHeatPumpSavingsValue
  const freeHeatPumpSavingsFormatted = showFreeHeatPumpSavingsBar
    ? formatFreeHeatPumpSavings(freeHeatPumpSavings)
    : null
  const renderFreeHeatPumpBar =
    showFreeHeatPumpSavingsBar && freeHeatPumpSavingsFormatted != null

  return (
    <div {...productCardConfig}>
      {image && (
        <ConditionalLink condition={hasLinks} config={primaryLinkConfig}>
          <div className={style["product-card__images-wrap"]}>
            <div className={style["product-card__images"]}>
              {hoverImage && (
                <GatsbyImage
                  image={hoverImage.gatsbyImageData}
                  className={style["product-card__image--hover"]}
                  loading="eager"
                />
              )}
              {image.gatsbyImageData ? (
                <GatsbyImage
                  image={image.gatsbyImageData}
                  className={style["product-card__image"]}
                />
              ) : (
                <img
                  src={image}
                  alt={image.alt || ""}
                  className={style["product-card__image-src"]}
                />
              )}
              {showRecommendation && <Recommendation productId={id} />}
              {showPrimarySaleBadge && (
                <div className={style["product-card__sale"]}>
                  <SaleBadge />
                </div>
              )}
              {/* {isClearance && (
                <div className={style["product-card__sale"]}>
                  <Combined />
                </div>
              )} */}
              {showPscOfferBadge && (
                <div className={style["product-card__sale"]}>
                  <Service />
                </div>
              )}
              {!isAvailable && (
                <div className={style["product-card__sold"]}>
                  <SoldSvg />
                </div>
              )}
              {hasAnyIcon && (
                <div className={style["product-card__icons"]}>
                  {iconComponents.map(
                    ({ condition, Component }, index) =>
                      condition && <Component key={index} />
                  )}
                </div>
              )}
              {isSearchResult && resolvedPricing?.storeItem && (
                <div className={style["product-card__shipping"]}>
                  <FastShipping />
                </div>
              )}
            </div>
          </div>
          {imageDisclaimer && (
            <center className={style["product-card__disclaimer"]}>
              Actual product not shown
            </center>
          )}
        </ConditionalLink>
      )}

      <div className={style["product-card__content"]}>
        <div className={style["product-card__header"]}>
          <div className={style["product-card__heading-wrap"]}>
            <ConditionalLink condition={hasLinks} config={primaryLinkConfig}>
              {heading && (
                <h4
                  className={style["product-card__heading"]}
                  children={<Superscript children={heading} />}
                />
              )}
            </ConditionalLink>
            {resolvedPricing && (
              <div className={style["product-card__pricing"]}>
                <Price
                  prices={resolvedPricing}
                  isAggregate={netoVariants && netoVariants.length > 0}
                  modifier={extended ? "split" : "stacked"}
                  isSearchResult={isSearchResult}
                />
              </div>
            )}
            <BundleSavings
              bundlePriceSavings={bundlePriceSavings}
              prices={resolvedPricing}
              isAggregate={netoVariants && netoVariants.length > 0}
              modifier={"card"}
              requireSavings
            />
          </div>
          {brand &&
            (brand.logo ? (
              <span className={style["product-card__brand"]}>
                <img alt={brand.name} src={brand.logo.url} />
              </span>
            ) : (
              <span className={style["product-card__brand"]}>
                <LogoSvg />
              </span>
            ))}
        </div>
        {dimensions && dimensions.length > 0 && (
          <div className={style["product-card__dimensions"]}>
            <dl>
              {dimensions.map(({ icon, label, name }) => (
                <Fragment key={`dimension-${name}-${label}`}>
                  <dt>{name}</dt>
                  <dd>
                    <span className={style["product-card__dimension-icon"]}>
                      {icon.url ? (
                        <img src={icon.url} alt={icon.alt} width={26} />
                      ) : (
                        icon
                      )}
                    </span>
                    <span className={style["product-card__dimension-label"]}>
                      {label}
                    </span>
                  </dd>
                </Fragment>
              ))}
            </dl>
          </div>
        )}
        {details && details.length > 0 && (
          <div className={style["product-card__details"]}>
            <dl>
              {details.map(({ name, value }, i) => (
                <div
                  key={`product-card-detail-${i}`}
                  className={style["product-card__detail"]}
                >
                  <dt>{name}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
        {description && (
          <p className={style["product-card__desc"]}>{description}</p>
        )}
      </div>
      <div className={style["product-card__buttons"]}>
        {links.length > 1 && (
          <div className={style["product-card__link"]} onClick={onClick}>
            <Button to={links[1].to} state={links[1].state} unstyled tertiary>
              {links[1].title}
              <FaAngleRight />
            </Button>
          </div>
        )}
      </div>

      {isSearchResult && resolvedPricing?.storeItem && (
        <div
          className={
            finance?.length > 0
              ? style["product-card__view-product"]
              : style["product-card__view-product-last"]
          }
        >
          <Button to={link.path} title={link.title} fullWidth noTarget>
            {link.title}
          </Button>
        </div>
      )}
      {renderFreeHeatPumpBar ? (
        <ConditionalLink condition={hasLinks} config={primaryLinkConfig}>
          <div className={style["product-card__heat-pump-savings"]}>
            {`Save up to $${freeHeatPumpSavingsFormatted} with FREE Heat Pump - T&C apply`}
          </div>
        </ConditionalLink>
      ) : (
        finance?.length > 0 && (
          <ConditionalLink condition={hasLinks} config={primaryLinkConfig}>
            <div className={style["product-card__finance"]}>
              <Finance finance={finance} card />
            </div>
          </ConditionalLink>
        )
      )}
    </div>
  )
}

export default ProductCard

const PreownedIcon = props => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 290 290">
    <circle cx="145" cy="145" r="110" fill="#005b8d"></circle>
    <g>
      <path
        fill="#fff"
        d="M92.8 190.1c0-.9.2-1.8.5-2.7s.8-1.7 1.4-2.4 1.4-1.3 2.3-1.7 2-.7 3.1-.7 2.6.3 3.6.9 1.8 1.4 2.2 2.4l-2.2 1.5c-.2-.4-.4-.8-.7-1.1s-.6-.5-.9-.7-.7-.3-1-.4c-.4 0-.7-.1-1-.1-.7 0-1.4.1-1.9.4s-1 .7-1.3 1.1c-.4.5-.6 1-.8 1.6s-.3 1.2-.3 1.8.1 1.3.3 1.9.5 1.1.9 1.6.8.8 1.4 1.1c.5.3 1.1.4 1.8.4s.7 0 1.1-.1.7-.2 1-.4.6-.4.9-.7.5-.6.7-1l2.4 1.4c-.2.6-.6 1.1-1 1.5s-.9.8-1.5 1.1-1.1.5-1.8.7-1.2.2-1.9.2c-1.1 0-2-.2-2.9-.7s-1.6-1-2.3-1.8-1.1-1.5-1.5-2.5c-.4-.9-.5-1.9-.5-2.8Zm25.7 5.1v2.5h-10.4v-14.9h10.2v2.5H111v3.6h6.3v2.4H111v3.9zm2.3 2.5v-14.9h6.6c.7 0 1.3.1 1.9.4s1.1.7 1.5 1.1c.4.5.8 1 1 1.6s.4 1.2.4 1.8c0 1-.2 1.8-.7 2.6s-1.1 1.4-2 1.7l3.4 5.6h-3.3l-3.1-5h-2.9v5h-2.9Zm2.9-7.6h3.6c.3 0 .5 0 .7-.2.2-.1.4-.3.6-.5s.3-.5.4-.8c0-.3.1-.6.1-.9s0-.7-.2-1-.3-.6-.5-.8-.4-.4-.7-.5c-.2-.1-.5-.2-.7-.2h-3.5v4.8Zm22-4.8h-4.8v12.4H138v-12.4h-4.8v-2.5h12.4v2.5Zm1.9 12.4v-14.9h2.9v14.9zm6 0v-14.9h10v2.5h-7.1v3.9h5.9v2.4h-5.9v6.2h-2.9Zm12.1 0v-14.9h2.9v14.9zm16.4-2.5v2.5h-10.4v-14.9h10.2v2.5h-7.3v3.6h6.3v2.4h-6.3v3.9zm2.3 2.5v-14.9h5.4c1.2 0 2.3.2 3.2.6s1.7.9 2.3 1.6 1.1 1.5 1.4 2.4.5 1.9.5 2.9-.2 2.2-.5 3.1-.8 1.7-1.5 2.4c-.6.7-1.4 1.2-2.3 1.5-.9.4-1.9.5-3 .5h-5.4Zm9.8-7.5c0-.7 0-1.4-.3-2-.2-.6-.5-1.1-.9-1.6s-.8-.8-1.4-1-1.2-.4-1.9-.4h-2.5v9.9h2.5c.7 0 1.4-.1 1.9-.4.6-.3 1-.6 1.4-1s.7-1 .9-1.6.3-1.3.3-2ZM81.8 218.7v-14.9h6.3c.7 0 1.3.1 1.9.4s1.1.7 1.5 1.1c.4.5.8 1 1 1.6s.4 1.2.4 1.8-.1 1.2-.3 1.8-.5 1.1-.9 1.6-.9.8-1.5 1.1-1.2.4-1.9.4h-3.5v5h-2.9Zm2.9-7.5h3.4c.5 0 1-.2 1.3-.7.4-.4.5-1 .5-1.8s0-.7-.2-1-.3-.6-.4-.8c-.2-.2-.4-.4-.6-.5s-.5-.2-.7-.2h-3.2v4.8Zm10.2 7.5v-14.9h6.6c.7 0 1.3.1 1.9.4s1.1.7 1.5 1.1c.4.5.8 1 1 1.6s.4 1.2.4 1.8c0 1-.2 1.8-.7 2.6s-1.1 1.4-2 1.7l3.4 5.6h-3.3l-3.1-5h-2.9v5h-2.9Zm2.9-7.5h3.6c.3 0 .5 0 .7-.2.2-.1.4-.3.6-.5s.3-.5.4-.8c0-.3.1-.6.1-.9s0-.7-.2-1-.3-.6-.5-.8-.4-.4-.7-.5-.5-.2-.7-.2h-3.5v4.8Zm21.5 5v2.5h-10.4v-14.9h10.2v2.5h-7.3v3.6h6.3v2.4h-6.3v3.9zm1.6-2v-2.5h6.4v2.5zm15.5 4.7q-1.65 0-3-.6c-.9-.4-1.7-1-2.3-1.7q-.9-1.05-1.5-2.4c-.6-1.35-.5-1.8-.5-2.8s.2-2 .6-2.9q.6-1.35 1.5-2.4c.7-.7 1.4-1.3 2.3-1.7s1.9-.6 2.9-.6 2.1.2 3 .7c.9.4 1.7 1 2.3 1.7q.9 1.05 1.5 2.4c.4.9.5 1.8.5 2.8s-.2 1.9-.5 2.9-.9 1.7-1.5 2.4-1.4 1.2-2.3 1.7c-.9.4-1.9.6-2.9.6Zm-4.4-7.6c0 .6 0 1.3.3 1.9s.5 1.1.9 1.6.8.8 1.4 1.1 1.2.4 1.8.4 1.3-.1 1.9-.4c.5-.3 1-.7 1.4-1.1.4-.5.7-1 .8-1.6q.3-.9.3-1.8c0-.9-.1-1.3-.3-1.9s-.5-1.1-.9-1.6-.8-.8-1.4-1.1c-.5-.3-1.1-.4-1.8-.4s-1.3.1-1.9.4c-.5.3-1 .7-1.4 1.1-.4.5-.6 1-.8 1.6q-.3.9-.3 1.8m18.5-7.5h2.7l1.7 4.9 1.7-4.9h2.7l-2.5 6.8 1.9 4.8 4.2-11.6h3.2l-6.1 14.9h-2.4l-2.6-6.3-2.6 6.3H150l-6-14.9h3.1l4.3 11.6 1.9-4.8-2.5-6.8Zm19.9 5.4v9.6h-2.9v-14.9h2.3l7.8 9.8v-9.8h2.9v14.9h-2.4zm23.6 7v2.5h-10.4v-14.9h10.2v2.5h-7.3v3.6h6.3v2.4h-6.3v3.9zm2.2 2.5v-14.9h5.4c1.2 0 2.3.2 3.2.6s1.7.9 2.3 1.6 1.1 1.5 1.4 2.4.5 1.9.5 2.9-.2 2.2-.5 3.1-.8 1.7-1.5 2.4c-.6.7-1.4 1.2-2.3 1.5-.9.4-1.9.5-3 .5h-5.4Zm9.9-7.4c0-.7 0-1.4-.3-2s-.5-1.1-.9-1.6-.8-.8-1.4-1-1.2-.4-1.9-.4h-2.5v9.9h2.5c.7 0 1.3-.1 1.9-.4s1-.6 1.4-1 .7-1 .9-1.6.3-1.3.3-2ZM156.6 81.9h12.2c4.8 0 8.8 3.9 8.8 8.8v47.7c0 4.8-3.9 8.8-8.8 8.8h-17.1v-10.6l-19.5 15.2 19.5 15.2v-9.8h17.1c10.3 0 18.7-8.4 18.7-18.7V90.8c0-10.3-8.4-18.7-18.7-18.7h-11.4l6 4.7-6.8 5.3Zm-27.2 65.3h-8.3c-4.8 0-8.8-3.9-8.8-8.8V90.7c0-4.8 3.9-8.8 8.8-8.8h14v9.9l19.5-15.2-19.5-15.2v10.5h-14c-10.3 0-18.7 8.4-18.7 18.7v47.7c0 10.3 8.4 18.7 18.7 18.7h9.2l-6.8-5.3 5.9-4.6Z"
      ></path>
    </g>
  </svg>
)

const SecondIcon = props => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 290 290">
    <circle cx="145" cy="145" r="110" fill="#005b8d"></circle>
    <g>
      <path d="M92.8 190.1c0-.9.2-1.8.5-2.7s.8-1.7 1.4-2.4 1.4-1.3 2.3-1.7 2-.7 3.1-.7 2.6.3 3.6.9 1.8 1.4 2.2 2.4l-2.2 1.5c-.2-.4-.4-.8-.7-1.1s-.6-.5-.9-.7-.7-.3-1-.4c-.4 0-.7-.1-1-.1-.7 0-1.4.1-1.9.4s-1 .7-1.3 1.1c-.4.5-.6 1-.8 1.6s-.3 1.2-.3 1.8.1 1.3.3 1.9.5 1.1.9 1.6.8.8 1.4 1.1c.5.3 1.1.4 1.8.4s.7 0 1.1-.1c.4 0 .7-.2 1-.4s.6-.4.9-.7.5-.6.7-1l2.4 1.4c-.2.6-.6 1.1-1 1.5s-.9.8-1.5 1.1-1.1.5-1.8.7c-.6.2-1.2.2-1.9.2-1.1 0-2-.2-2.9-.7s-1.6-1-2.3-1.8-1.1-1.5-1.5-2.5c-.4-.9-.5-1.9-.5-2.8Zm25.7 5.1v2.5h-10.4v-14.9h10.2v2.5H111v3.6h6.3v2.4H111v3.9zm2.3 2.5v-14.9h6.6c.7 0 1.3.1 1.9.4s1.1.7 1.5 1.1c.4.5.8 1 1 1.6s.4 1.2.4 1.8c0 1-.2 1.8-.7 2.6s-1.1 1.4-2 1.7l3.4 5.6h-3.3l-3.1-5h-2.9v5h-2.9Zm2.9-7.6h3.6c.3 0 .5 0 .7-.2.2-.1.4-.3.6-.5s.3-.5.4-.8c0-.3.1-.6.1-.9s0-.7-.2-1c-.1-.3-.3-.6-.5-.8s-.4-.4-.7-.5c-.2-.1-.5-.2-.7-.2h-3.5v4.8Zm22-4.8h-4.8v12.4H138v-12.4h-4.8v-2.5h12.4v2.5Zm1.9 12.4v-14.9h2.9v14.9zm6 0v-14.9h10v2.5h-7.1v3.9h5.9v2.4h-5.9v6.2h-2.9Zm12.1 0v-14.9h2.9v14.9zm16.4-2.5v2.5h-10.4v-14.9h10.2v2.5h-7.3v3.6h6.3v2.4h-6.3v3.9zm2.3 2.5v-14.9h5.4c1.2 0 2.3.2 3.2.6s1.7.9 2.3 1.6 1.1 1.5 1.4 2.4.5 1.9.5 2.9-.2 2.2-.5 3.1-.8 1.7-1.5 2.4c-.6.7-1.4 1.2-2.3 1.5-.9.4-1.9.5-3 .5h-5.4Zm9.8-7.5c0-.7 0-1.4-.3-2s-.5-1.1-.9-1.6-.8-.8-1.4-1-1.2-.4-1.9-.4h-2.5v9.9h2.5c.7 0 1.3-.1 1.9-.4s1-.6 1.4-1 .7-1 .9-1.6.3-1.3.3-2Zm-82 17.5s-.3-.2-.5-.4c-.3-.2-.6-.3-.9-.5-.4-.2-.8-.3-1.2-.4s-.9-.2-1.3-.2c-.8 0-1.4.1-1.8.4q-.6.45-.6 1.2c0 .75 0 .6.2.8.1.2.4.4.6.5.3.2.6.3 1.1.4.4.1.9.3 1.5.4.7.2 1.4.4 2 .6s1.1.5 1.5.8.7.7 1 1.2.3 1 .3 1.7-.2 1.5-.5 2.1-.7 1-1.2 1.4-1.1.6-1.8.8-1.4.2-2.1.2c-1.1 0-2.2-.2-3.3-.5s-2.1-.8-2.9-1.4l1.3-2.5c.1.1.4.3.7.5q.45.3 1.2.6c.75.3.9.4 1.5.5.5.1 1.1.2 1.7.2q2.4 0 2.4-1.5c0-1.5 0-.6-.3-.8-.2-.2-.4-.4-.8-.6-.3-.2-.7-.3-1.2-.5-.5-.1-1-.3-1.6-.5-.7-.2-1.3-.4-1.9-.6s-1-.5-1.3-.8-.6-.7-.8-1.1q-.3-.6-.3-1.5c0-.9.1-1.4.4-2q.45-.9 1.2-1.5c.5-.4 1.1-.7 1.7-.9q1.05-.3 2.1-.3c1.05 0 2 .2 2.9.5s1.7.7 2.3 1.2l-1.3 2.4Zm14.3 8.5v2.5h-10.4v-14.9h10.2v2.5H119v3.6h6.3v2.4H119v3.9zm1.1-5c0-.9.2-1.8.5-2.7s.8-1.7 1.4-2.4 1.4-1.3 2.3-1.7 2-.7 3.1-.7 2.6.3 3.6.9 1.8 1.4 2.2 2.4l-2.2 1.5c-.2-.4-.4-.8-.7-1.1s-.6-.5-.9-.7-.7-.3-1-.4c-.4 0-.7-.1-1-.1-.7 0-1.4.1-1.9.4s-1 .7-1.3 1.1c-.4.5-.6 1-.8 1.6s-.3 1.2-.3 1.8.1 1.3.3 1.9.5 1.1.9 1.6.8.8 1.4 1.1c.5.3 1.1.4 1.8.4s.7 0 1.1-.1c.4 0 .7-.2 1-.4s.6-.4.9-.7.5-.6.7-1l2.4 1.4c-.2.6-.6 1.1-1 1.5s-.9.8-1.5 1.1-1.1.5-1.8.7c-.6.2-1.2.2-1.9.2-1.1 0-2-.2-2.9-.7s-1.6-1-2.3-1.8-1.1-1.5-1.5-2.5c-.4-.9-.5-1.9-.5-2.8Zm21.6 7.7q-1.65 0-3-.6c-.9-.4-1.7-1-2.3-1.7q-.9-1.05-1.5-2.4c-.6-1.35-.5-1.8-.5-2.8s.2-2 .6-2.9q.6-1.35 1.5-2.4c.7-.7 1.4-1.3 2.3-1.7s1.9-.6 2.9-.6 2.1.2 3 .7c.9.4 1.7 1 2.3 1.7q.9 1.05 1.5 2.4c.4.9.5 1.8.5 2.8s-.2 1.9-.5 2.9q-.6 1.35-1.5 2.4c-.9 1.05-1.4 1.2-2.3 1.7-.9.4-1.9.6-2.9.6Zm-4.4-7.6c0 .6 0 1.3.3 1.9.2.6.5 1.1.9 1.6s.8.8 1.4 1.1 1.2.4 1.8.4 1.3-.1 1.9-.4c.5-.3 1-.7 1.4-1.1.4-.5.7-1 .8-1.6q.3-.9.3-1.8c0-.9-.1-1.3-.3-1.9s-.5-1.1-.9-1.6-.8-.8-1.4-1.1c-.5-.3-1.1-.4-1.8-.4s-1.3.1-1.9.4c-.5.3-1 .7-1.4 1.1-.4.5-.6 1-.8 1.6q-.3.9-.3 1.8m16.8-2.1v9.6h-2.9v-14.9h2.3l7.8 9.8v-9.8h2.9v14.9h-2.4zm13.2 9.5v-14.9h5.4c1.2 0 2.3.2 3.2.6s1.7.9 2.3 1.6 1.1 1.5 1.4 2.4.5 1.9.5 2.9-.2 2.2-.5 3.1-.8 1.7-1.5 2.4c-.6.7-1.4 1.2-2.3 1.5-.9.4-1.9.5-3 .5h-5.4Zm9.8-7.4c0-.7 0-1.4-.3-2s-.5-1.1-.9-1.6-.8-.8-1.4-1-1.2-.4-1.9-.4h-2.5v9.9h2.5c.7 0 1.3-.1 1.9-.4s1-.6 1.4-1 .7-1 .9-1.6.3-1.3.3-2Z"></path>
      <path d="M166.2 66.4c.3 3.1-.5 6.5-2 9.8q1.05 1.35 1.5 3l.2.6c.2-.3.3-.5.5-.8 2.3-4.2 3.4-8.8 3.1-12.9-.4-4.4-2.3-7.7-5.4-9.5l-1.6 2.8c2.1 1.2 3.5 3.6 3.7 6.9Zm16.9 89.6-5-57.2c-.1-1.5-.9-3-2-4l-8.6-9.7 2.2 7.3 4.2 4.7c.6.6.9 1.2 1 2l5 57.2c0 .7-.2 1.5-.6 2-.5.6-1.2.9-1.9 1l-21.6 1.9-1.2 3.3 23.1-2c1.6-.1 3.1-.9 4.1-2.1s1.5-2.8 1.4-4.4Z"></path>
      <path d="M145.2 168.9c-.7 0-1.4-.1-2.1-.4l-45.6-16.6c-1.5-.5-2.7-1.7-3.4-3.1-.7-1.5-.8-3.1-.2-4.6l19.7-54c.5-1.5 1.6-2.7 3-3.3l22.8-15.1c1.1-.7 2.3-1.1 3.6-1.1s1.5.1 2.2.4l13.3 4.8c1.9.7 3.4 2.3 4 4.3l7.8 26.3c.6 1.4.7 3 .2 4.5l-19.7 54c-.9 2.4-3.1 4-5.7 4Zm-2.2-95c-.7 0-1.3.2-1.8.5l-23 15.3h-.1c-.7.4-1.2.9-1.5 1.6l-19.7 54c-.3.7-.2 1.5 0 2.1.3.7.9 1.2 1.6 1.4l45.6 16.6c.3.1.6.2 1 .2 1.2 0 2.2-.7 2.6-1.8l19.7-54c.3-.7.2-1.5 0-2.1 0-.1-7.8-26.6-7.8-26.6-.3-1-1.1-1.8-2-2.2l-13.3-4.8c-.4-.1-.7-.2-1.1-.2Z"></path>
      <path d="M150.9 90.1c-1.7 0-3.3-.4-4.7-1.2l1.6-2.8c1.3.7 2.7.9 4.3.7l.5 3.2c-.6 0-1.1.1-1.7.1"></path>
      <path d="M146.9 96c-1 0-2-.2-2.9-.5-2.2-.8-3.9-2.4-4.8-4.4-1-2.1-1.1-4.4-.3-6.6 1.2-3.4 4.5-5.6 8.1-5.6s2 .2 2.9.5c2.2.8 3.9 2.4 4.8 4.4 1 2.1 1.1 4.4.3 6.6-1.2 3.4-4.5 5.6-8.1 5.6m0-13.1c-1.9 0-3.6 1.2-4.3 3-.4 1.1-.4 2.4.2 3.5.5 1.1 1.4 1.9 2.6 2.3.5.2 1 .3 1.6.3 1.9 0 3.6-1.2 4.3-3 .4-1.1.4-2.4-.2-3.5-.5-1.1-1.4-1.9-2.6-2.3-.5-.2-1-.3-1.6-.3"></path>
      <path d="M146.2 88.9c-3.1-1.7-5-5.1-5.4-9.5-.3-4.1.7-8.7 3.1-12.9 2.3-4.2 5.7-7.5 9.3-9.4 3.9-2 7.7-2.1 10.8-.4l-1.6 2.8c-2.1-1.2-4.9-1-7.8.5-3.1 1.6-5.9 4.5-8 8.1-2 3.6-3 7.6-2.7 11.1.3 3.3 1.6 5.7 3.7 6.9l-1.6 2.8Z"></path>
      <path d="M145.7 120c0-.2-.2-.5-.6-1s-.8-1-1.4-1.6-1.3-1.3-2-1.9c-.8-.7-1.6-1.2-2.4-1.7l-2.6 6.6 1.4 1c1.5 1.1 2.8 2.2 3.9 3.2s2 2.1 2.6 3.3.9 2.4 1 3.7c0 1.3-.2 2.8-.8 4.4s-1.6 3.3-2.8 4.3c-1.1 1-2.4 1.8-3.8 2.2s-2.9.5-4.5.4-3.1-.5-4.7-1l-2.2 5.6-3.3-1.3 2.2-5.7c-2.2-1.1-4.2-2.4-6.1-3.9s-3.5-3.3-4.8-5.1l6.2-5.6c0 .2.3.6.7 1.2s1 1.3 1.7 2 1.5 1.5 2.5 2.3 2 1.5 3.1 2.1l2.5-6.4-2.1-1.6c-1.4-1.1-2.6-2.2-3.5-3.2s-1.6-2-2.1-3.1c-.5-1-.7-2.1-.7-3.2s.3-2.3.8-3.6 1.5-3.1 2.6-4.1 2.3-1.9 3.7-2.4c1.3-.6 2.8-.8 4.3-.8s3.1.2 4.7.6l1.9-4.9 3.3 1.3-1.9 4.9c2 1 3.8 2.2 5.4 3.7s2.9 2.8 3.9 4.1l-6 5.2Zm-13.2-5.6c-.3.8-.3 1.6.1 2.2s1.1 1.4 2.1 2.2l2.3-6q-3.45-1.05-4.5 1.5Zm3.4 19q.45-1.2-.3-2.4c-.5-.7-1.2-1.5-2.2-2.3l-2.3 5.8c2.5.9 4.1.5 4.7-1.1Z"></path>
    </g>{" "}
  </svg>
)

const RunoutIcon = props => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 290 290">
    <circle cx="145" cy="145" r="110" fill="#005b8d"></circle>
    <g>
      <path d="M124 188.1v9.6h-2.9v-14.9h2.3l7.8 9.8v-9.8h2.9v14.9h-2.4zm23.6 7.1v2.5h-10.4v-14.9h10.2v2.5h-7.3v3.6h6.3v2.4h-6.3v3.9zm7.4-12.4h2.7l1.7 4.9 1.7-4.9h2.7l-2.5 6.8 1.9 4.8 4.2-11.6h3.2l-6.1 14.9h-2.4l-2.6-6.3-2.6 6.3h-2.4l-6-14.9h3.1l4.3 11.6 1.9-4.8-2.5-6.8Zm-53.7 35.9v-14.9h6.6c.7 0 1.3.1 1.9.4s1.1.7 1.5 1.1c.4.5.8 1 1 1.6s.4 1.2.4 1.8c0 1-.2 1.8-.7 2.6s-1.1 1.4-2 1.7l3.4 5.6h-3.3l-3.1-5h-2.9v5h-2.9Zm2.9-7.5h3.6c.3 0 .5 0 .7-.2.2-.1.4-.3.6-.5s.3-.5.4-.8c0-.3.1-.6.1-.9s0-.7-.2-1c-.1-.3-.3-.6-.5-.8s-.4-.4-.7-.5c-.2-.1-.5-.2-.7-.2H104v4.8Zm17.4 5.1c.7 0 1.3-.1 1.7-.4.5-.3.8-.6 1.1-1.1.3-.4.5-1 .6-1.5.1-.6.2-1.2.2-1.8v-7.6h2.9v7.6c0 1-.1 2-.4 2.9q-.3 1.35-1.2 2.4c-.5.7-1.2 1.2-2 1.6q-1.2.6-3 .6c-1.8 0-2.2-.2-3-.6s-1.5-1-2-1.6c-.5-.7-.9-1.5-1.1-2.4s-.3-1.8-.3-2.8V204h2.9v7.6c0 .6 0 1.2.2 1.8q.15.9.6 1.5c.3.4.7.8 1.1 1.1.5.3 1 .4 1.7.4Zm12.4-7.1v9.6h-2.9v-14.9h2.3l7.8 9.8v-9.8h2.9v14.9h-2.4zm19.5 9.7q-1.65 0-3-.6c-.9-.4-1.7-1-2.3-1.7q-.9-1.05-1.5-2.4c-.4-.9-.5-1.8-.5-2.8s.2-2 .6-2.9.9-1.7 1.5-2.4c.7-.7 1.4-1.3 2.3-1.7s1.9-.6 2.9-.6 2.1.2 3 .7c.9.4 1.7 1 2.3 1.7q.9 1.05 1.5 2.4c.3.9.5 1.8.5 2.8s-.2 1.9-.5 2.9-.9 1.7-1.5 2.4-1.4 1.2-2.3 1.7c-.9.4-1.9.6-2.9.6Zm-4.3-7.6c0 .6 0 1.3.3 1.9s.5 1.1.9 1.6.8.8 1.4 1.1c.5.3 1.2.4 1.8.4s1.3-.1 1.9-.4c.5-.3 1-.7 1.4-1.1.4-.5.7-1 .8-1.6q.3-.9.3-1.8c0-.9-.1-1.3-.3-1.9s-.5-1.1-.9-1.6-.8-.8-1.4-1.1c-.5-.3-1.1-.4-1.8-.4s-1.3.1-1.9.4c-.5.3-1 .7-1.4 1.1-.4.5-.6 1-.8 1.6q-.3.9-.3 1.8m20.1 5c.7 0 1.3-.1 1.7-.4.5-.3.8-.6 1.1-1.1.3-.4.5-1 .6-1.5.1-.6.2-1.2.2-1.8v-7.6h2.9v7.6c0 1-.1 2-.4 2.9q-.3 1.35-1.2 2.4c-.5.7-1.2 1.2-2 1.6q-1.2.6-3 .6c-1.8 0-2.2-.2-3-.6s-1.5-1-2-1.6c-.5-.7-.9-1.5-1.1-2.4s-.3-1.8-.3-2.8V204h2.9v7.6c0 .6 0 1.2.2 1.8q.15.9.6 1.5c.3.4.7.8 1.1 1.1.5.3 1 .4 1.7.4Zm20.7-9.9h-4.8v12.4h-2.9v-12.4h-4.8v-2.5h12.4v2.5ZM144.6 148c-.8 0-1.4-.5-1.5-1.3v-.4c-2.4-13.9-5.1-29.7-29.1-30.9-.8 0-1.4-.6-1.5-1.4 0-.8.5-1.5 1.2-1.6 19.4-4.2 26.2-12.5 29.2-35.7.1-.8.8-1.4 1.6-1.3.8 0 1.4.6 1.5 1.4 1.5 16.4 6.1 32.1 29.6 35.6.8.1 1.4.8 1.3 1.6 0 .8-.7 1.4-1.5 1.5-16.4.7-26 10.9-29.3 31.3-.1.7-.8 1.3-1.5 1.3Zm-.1-78.9c-.9 0-1.5-.7-1.5-1.5v-9.5c0-.9.7-1.5 1.5-1.5s1.5.7 1.5 1.5v9.5c0 .9-.7 1.5-1.5 1.5"></path>
      <path d="M125.1 97.5c-.4 0-.7-.1-1-.4l-18-16.3c-.6-.6-.7-1.5-.1-2.2.6-.6 1.5-.7 2.2-.1l18 16.3c.6.6.7 1.5.1 2.2-.3.3-.7.5-1.1.5Zm38.5.4c-.4 0-.8-.2-1.1-.5-.6-.6-.6-1.6 0-2.2l17.1-17.1c.6-.6 1.6-.6 2.2 0s.6 1.6 0 2.2l-17.1 17.1c-.3.3-.7.5-1.1.5m17.9 54.7c-.4 0-.7-.1-1-.4l-18-16.3c-.6-.6-.7-1.5-.1-2.2.6-.6 1.5-.7 2.2-.1l18 16.3c.6.6.7 1.5.1 2.2-.3.3-.7.5-1.1.5Zm-73.5.5c-.4 0-.8-.2-1.1-.5-.6-.6-.6-1.6 0-2.2l17.1-17.1c.6-.6 1.6-.6 2.2 0s.6 1.6 0 2.2l-17.1 17.1c-.3.3-.7.5-1.1.5m36.5 13.9c-.9 0-1.5-.7-1.5-1.5V156c0-.9.7-1.5 1.5-1.5s1.5.7 1.5 1.5v9.5c0 .9-.7 1.5-1.5 1.5m50.8-51.5h-9.7c-.9 0-1.5-.7-1.5-1.5s.7-1.5 1.5-1.5h9.7c.9 0 1.5.7 1.5 1.5s-.7 1.5-1.5 1.5m-91.9 0h-9.7c-.9 0-1.5-.7-1.5-1.5s.7-1.5 1.5-1.5h9.7c.9 0 1.5.7 1.5 1.5s-.7 1.5-1.5 1.5"></path>
    </g>
  </svg>
)

const DisplayIcon = props => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 290 290">
    <circle cx="145.3" cy="145.3" r="110" fill="#005b8d"></circle>
    <g>
      <path
        fill="#fff"
        d="M93 181.4c0-.9.2-1.8.5-2.7s.8-1.7 1.4-2.4 1.4-1.3 2.3-1.7 2-.7 3.1-.7 2.6.3 3.6.9 1.8 1.4 2.2 2.4l-2.2 1.5c-.2-.4-.4-.8-.7-1.1s-.6-.5-.9-.7-.7-.3-1-.4c-.4 0-.7-.1-1-.1-.7 0-1.4.1-1.9.4s-1 .7-1.3 1.1c-.4.5-.6 1-.8 1.6s-.3 1.2-.3 1.8.1 1.3.3 1.9.5 1.1.9 1.6.8.8 1.4 1.1c.5.3 1.1.4 1.8.4s.7 0 1.1-.1c.4 0 .7-.2 1-.4s.6-.4.9-.7.5-.6.7-1l2.4 1.4c-.2.6-.6 1.1-1 1.5s-.9.8-1.5 1.1-1.1.5-1.8.7c-.6.2-1.2.2-1.9.2-1.1 0-2-.2-2.9-.7s-1.6-1-2.3-1.8-1.1-1.5-1.5-2.5c-.4-.9-.5-1.9-.5-2.8Zm25.8 5.1v2.5h-10.4v-14.9h10.2v2.5h-7.3v3.6h6.3v2.4h-6.3v3.9zm2.2 2.5v-14.9h6.6c.7 0 1.3.1 1.9.4s1.1.7 1.5 1.1c.4.5.8 1 1 1.6s.4 1.2.4 1.8c0 1-.2 1.8-.7 2.6s-1.1 1.4-2 1.7l3.4 5.6h-3.3l-3.1-5h-2.9v5h-2.9Zm3-7.5h3.6c.3 0 .5 0 .7-.2.2-.1.4-.3.6-.5s.3-.5.4-.8c0-.3.1-.6.1-.9s0-.7-.2-1c-.1-.3-.3-.6-.5-.8s-.4-.4-.7-.5c-.2-.1-.5-.2-.7-.2h-3.5v4.8Zm22-4.9h-4.8V189h-2.9v-12.4h-4.8v-2.5h12.4v2.5Zm1.8 12.4v-14.9h2.9V189zm6.1 0v-14.9h10v2.5h-7.1v3.9h5.9v2.4h-5.9v6.2h-2.9Zm12 0v-14.9h2.9V189zm16.4-2.5v2.5h-10.4v-14.9h10.2v2.5h-7.3v3.6h6.3v2.4h-6.3v3.9zm2.3 2.5v-14.9h5.4c1.2 0 2.3.2 3.2.6s1.7.9 2.3 1.6 1.1 1.5 1.4 2.4.5 1.9.5 2.9-.2 2.2-.5 3.1-.8 1.7-1.5 2.4c-.6.7-1.4 1.2-2.3 1.5-.9.4-1.9.5-3 .5h-5.4Zm9.9-7.5c0-.7 0-1.4-.3-2s-.5-1.1-.9-1.6-.8-.8-1.4-1-1.2-.4-1.9-.4h-2.5v9.9h2.5c.7 0 1.3-.1 1.9-.4s1-.6 1.4-1 .7-1 .9-1.6.3-1.3.3-2ZM104 210.1v-14.9h5.4c1.2 0 2.3.2 3.2.6s1.7.9 2.3 1.6 1.1 1.5 1.4 2.4.5 1.9.5 2.9-.2 2.2-.5 3.1-.8 1.7-1.5 2.4c-.6.7-1.4 1.2-2.3 1.5-.9.4-1.9.5-3 .5h-5.4Zm9.8-7.5c0-.7 0-1.4-.3-2s-.5-1.1-.9-1.6-.8-.8-1.4-1-1.2-.4-1.9-.4h-2.5v9.9h2.5c.7 0 1.3-.1 1.9-.4s1-.6 1.4-1 .7-1 .9-1.6.3-1.3.3-2Zm5.2 7.5v-14.9h2.9v14.9zm15-11.1s-.3-.2-.5-.4c-.3-.2-.6-.3-.9-.5-.4-.2-.8-.3-1.2-.4s-.9-.2-1.3-.2c-.8 0-1.4.1-1.8.4q-.6.45-.6 1.2c0 .75 0 .6.2.8.1.2.4.4.6.5.3.2.6.3 1.1.4.4.1.9.3 1.5.4.7.2 1.4.4 2 .6s1.1.5 1.5.8.7.7 1 1.2.3 1 .3 1.7-.2 1.5-.5 2.1-.7 1-1.2 1.4-1.1.6-1.8.8-1.4.2-2.1.2c-1.1 0-2.2-.2-3.3-.5s-2.1-.8-2.9-1.4l1.3-2.5c.1.1.4.3.7.5q.45.3 1.2.6c.75.3.9.4 1.5.5.5.1 1.1.2 1.7.2q2.4 0 2.4-1.5c0-1.5 0-.6-.3-.8-.2-.2-.4-.4-.8-.6-.3-.2-.7-.3-1.2-.5-.5-.1-1-.3-1.6-.5-.7-.2-1.3-.4-1.9-.6s-1-.5-1.3-.8-.6-.7-.8-1.1q-.3-.6-.3-1.5c0-.9.1-1.4.4-2q.45-.9 1.2-1.5c.5-.4 1.1-.7 1.7-.9q1.05-.3 2.1-.3c1.05 0 2 .2 2.9.5s1.7.7 2.3 1.2l-1.3 2.4Zm4 11.1v-14.9h6.3c.7 0 1.3.1 1.9.4s1.1.7 1.5 1.1c.4.5.8 1 1 1.6s.4 1.2.4 1.8-.1 1.2-.3 1.8-.5 1.1-.9 1.6-.9.8-1.5 1.1-1.2.4-1.9.4H141v5h-2.9Zm2.9-7.6h3.4c.5 0 1-.2 1.3-.7.4-.4.5-1 .5-1.8s0-.7-.2-1-.3-.6-.4-.8c-.2-.2-.4-.4-.6-.5s-.5-.2-.7-.2H141v4.8Zm10.2 7.6v-14.9h2.9v12.4h7.6v2.5zm16.5-15h2.6l5.7 14.9h-3l-1.4-3.7h-5.2l-1.4 3.7h-3zm3.4 9.2-2.1-5.9-2.1 5.9zm6.5-9.2 3.7 7.2 3.7-7.2h3.1l-5.4 9.7v5.2h-2.9v-5.3l-5.4-9.7h3.2Zm-55.6 36v-9.9l-3.9 7.4h-1.6l-3.9-7.4v9.9h-2.9v-14.9h3.1l4.4 8.5 4.5-8.5h3.1v14.9h-2.9Zm12.4.1q-1.65 0-3-.6c-.9-.4-1.7-1-2.3-1.7q-.9-1.05-1.5-2.4c-.6-1.35-.5-1.8-.5-2.8s.2-2 .6-2.9q.6-1.35 1.5-2.4c.7-.7 1.4-1.3 2.3-1.7s1.9-.6 2.9-.6 2.1.2 3 .7c.9.4 1.7 1 2.3 1.7q.9 1.05 1.5 2.4c.4.9.5 1.8.5 2.8s-.2 1.9-.5 2.9q-.6 1.35-1.5 2.4c-.9 1.05-1.4 1.2-2.3 1.7-.9.4-1.9.6-2.9.6Zm-4.3-7.6c0 .6 0 1.3.3 1.9.2.6.5 1.1.9 1.6s.8.8 1.4 1.1 1.2.4 1.8.4 1.3-.1 1.9-.4c.5-.3 1-.7 1.4-1.1.4-.5.7-1 .8-1.6q.3-.9.3-1.8c0-.9-.1-1.3-.3-1.9s-.5-1.1-.9-1.6-.8-.8-1.4-1.1c-.5-.3-1.1-.4-1.8-.4s-1.3.1-1.9.4c-.5.3-1 .7-1.4 1.1-.4.5-.6 1-.8 1.6q-.3.9-.3 1.8m13.9 7.5v-14.9h5.4c1.2 0 2.3.2 3.2.6s1.7.9 2.3 1.6 1.1 1.5 1.4 2.4.5 1.9.5 2.9-.2 2.2-.5 3.1-.8 1.7-1.5 2.4c-.6.7-1.4 1.2-2.3 1.5-.9.4-1.9.5-3 .5H144Zm9.8-7.5c0-.7 0-1.4-.3-2s-.5-1.1-.9-1.6-.8-.8-1.4-1-1.2-.4-1.9-.4h-2.5v9.9h2.5c.7 0 1.3-.1 1.9-.4s1-.6 1.4-1 .7-1 .9-1.6.3-1.3.3-2Zm15.6 5v2.5h-10.4v-14.9h10.2v2.5h-7.3v3.6h6.3v2.4h-6.3v3.9zm2.3 2.5v-14.9h2.9v12.4h7.6v2.5zm26.1-143.5L153 62.1c-4.8-2.7-10.6-2.7-15.4 0L92.9 87.6c-2.5 1.4-4.1 4.1-4.1 7v29.2c0 2.9 1.6 5.6 4.1 7l44.7 25.5c4.8 2.7 10.6 2.7 15.4 0l44.7-25.5c2.5-1.4 4.1-4.1 4.1-7V94.6c0-2.9-1.6-5.6-4.1-7M158 81.9v3.2l-5-2.9c-1.8-1-3.8-1.7-5.9-1.9v-9.1c1.4.2 2.8.7 4.1 1.4l7.6 4.5c-.5 1.6-.7 3.2-.8 4.9Zm-14.6-1.6c-2.1.2-4.1.9-5.9 1.9L108.8 99l-7.3-4.3 37.9-22.1c1.3-.7 2.6-1.2 4.1-1.4v9.1Zm-4 5c3.6-2.1 8.1-2.1 11.7 0L178 101l-26.9 15.7c-3.6 2.1-8.1 2.1-11.7 0L112.5 101zM181.7 99l-7.3-4.3V88c0-.6 0-1.2.1-1.8l14.5 8.4-7.3 4.3Zm-10.9-11v4.6l-9.1-5.3V82c0-3.2 1.2-6.3 3.4-8.7l8.9 5.1c-2.1 2.8-3.2 6.2-3.2 9.7Zm-78.4 35.8v-22.4c.1 0 .3.2.4.3l5 2.9v25l-3.2-1.8c-1.4-.8-2.2-2.3-2.2-3.9Zm41.9 26.6-32.8-18.7v-25l32.8 18.7zm5.1 2.8-1.4-.8v-25c4.5 2.5 10 2.5 14.6 0v25l-1.4.8c-3.7 2-8.1 2-11.8 0m16.8-27.8 32.8-18.7v25l-32.8 18.7zm39.7 2.3-3.2 1.8v-25l5-2.9c.2 0 .3-.2.4-.3v22.4c0 1.6-.9 3.1-2.2 3.9Zm0-29.1-44.7 25.5c-3.7 2-8.1 2-11.8 0L94.7 98.6c-2.1-1.2-2.9-3.9-1.7-6.1.4-.7 1-1.3 1.7-1.7l44.7-25.5c3.7-2.1 8.1-2.1 11.8 0l10.7 6.1c-.6.7-1.1 1.5-1.6 2.3l-7.3-4.2c-4.8-2.7-10.6-2.7-15.4 0L97 93.2c-.9.5-1.2 1.6-.7 2.5.2.3.4.5.7.7l40.6 23.7c4.8 2.8 10.7 2.8 15.4 0l40.6-23.7c.9-.5 1.2-1.6.7-2.5-.2-.3-.4-.5-.7-.7l-18-10.5c.4-.9.9-1.7 1.5-2.5l18.7 10.7c2.1 1.2 2.9 3.9 1.7 6.1-.4.7-1 1.3-1.7 1.7Z"
      ></path>
    </g>
  </svg>
)

const SalesIcon = props => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 119">
    <path
      fill="#E10E49"
      d="M12.82 31.1a42.72 42.72 0 0 0 0 61 44.2 44.2 0 0 0 61.87 0 42.72 42.72 0 0 0 0-61C66.03 22.54 43.3 0 43.3 0S21.24 22.78 12.82 31.1"
    ></path>
    <path
      fill="#FFF"
      fillRule="nonzero"
      d="M20.85 72.2c5.31 0 8.25-2.84 8.25-7.16 0-2.95-1.95-5.03-5.95-5.86-4.1-.86-5.12-1.66-5.12-2.98 0-1.7 1.25-2.81 4.64-2.81 2.4 0 4.35.7 5.7 2.14h.13v-2.01c0-1.83-1.48-4-6.6-4-5.15 0-8.22 3.45-8.22 7.1 0 2.98 2.11 4.83 5.82 5.63 4.64 1 5.22 1.67 5.22 3.17 0 1.76-1.06 2.88-4.35 2.88a8.94 8.94 0 0 1-6.11-2.37h-.1v1.95c0 2.6 2.27 4.32 6.69 4.32m18.78-.1c3.84 0 6.53-2.52 6.53-6.17v-3.97c0-5.95-3.46-7.74-7.3-7.74-4.54 0-6.27 2.3-6.27 4.22v1.38h.16c.64-.8 2.37-1.98 5.47-1.98 2.56 0 3.72.73 3.72 3.16v.23h-1.5c-6.34 0-8.58 2.33-8.58 5.4 0 3.17 2.81 5.48 6.9 5.48h.87zm-.41-3.48c-1.9 0-2.98-.8-2.98-2.14 0-1.41.93-2.18 4.2-2.18h1.53v1.66c0 1.76-.9 2.66-2.75 2.66m15.96 3.36c1.9 0 2.88-.74 2.88-2.6v-.92h-1.15c-2.01 0-2.91-.54-2.91-2.27V51.66c0-1.63-.93-2.88-3.07-2.88H49.7V66.6c0 3.5 1.92 5.38 5.47 5.38zm12.48.22c4.74 0 6.24-1.7 6.24-3.9v-1.34h-.16c-1.37 1.28-3.48 1.63-5.28 1.63-2.52 0-4.5-1.38-4.83-3.55h2.82c5.7 0 8.1-1.03 8.1-4.2 0-4.12-3.33-6.68-6.92-6.68-4.93 0-8.45 3.61-8.45 9.37 0 5.28 4.2 8.67 8.48 8.67M65.82 62H63.6l.03-.58c.1-1.98 1.54-3.68 3.62-3.68 1.98 0 3.26.86 3.26 2.6 0 1.18-.88 1.63-4.7 1.66z"
    ></path>
  </svg>
)

export const query = graphql`
  fragment ProductCard on DatoCmsProduct {
    name
    id
    slug
    path
    setCardImage
    freeDelivery
    motorised
    clearliftCompatible
    uvFree
    uvIncluded
    freeService
    freeHeatPump
    freeHeatPumpSavings
    bundlePriceSavings
    infrared
    viewer
    heroImage {
      gatsbyImageData(
        layout: FULL_WIDTH
        imgixParams: {
          h: "330"
          fit: "fill"
          fill: "solid"
          w: "500"
          q: 60
          auto: "format"
        }
      )
      alt
    }
    heroImageTwo {
      gatsbyImageData(
        layout: FULL_WIDTH
        imgixParams: {
          h: "330"
          fit: "fill"
          fill: "solid"
          w: "500"
          q: 60
          auto: "format"
        }
      )
      alt
    }
    heroImageThree {
      gatsbyImageData(
        layout: FULL_WIDTH
        imgixParams: {
          h: "330"
          fit: "fill"
          fill: "solid"
          w: "500"
          q: 60
          auto: "format"
        }
      )
      alt
    }
    specsImage: specificationsImage {
      gatsbyImageData(
        layout: FULL_WIDTH
        imgixParams: {
          h: "330"
          fit: "fill"
          fill: "solid"
          fillColor: "00ffffff"
          w: "500"
          q: 60
          auto: "format"
        }
      )
      alt
    }
    netoProduct {
      sku
      name
      seats
      retailPrice
      rrp
      saveUpTo
      currency
      promotionStartDate
      promotionPrice
      promotionExpiryDate
      ...ProductAggregateSpecs
      finance {
        id
        slug
        name
        repaymentOptions
        logo {
          gatsbyImageData(
            layout: CONSTRAINED
            imgixParams: {
              h: "22"
              fit: "fill"
              fill: "solid"
              fillColor: "00ffffff"
              w: "22"
              q: 60
              dpr: 2
            }
          )
          alt
        }
        wideIcon
        logoWide: logo {
          gatsbyImageData(
            layout: CONSTRAINED
            imgixParams: {
              h: "22"
              fit: "fill"
              fill: "solid"
              fillColor: "00ffffff"
              w: "44"
              q: 60
              dpr: 2
            }
          )
          alt
        }
      }
    }
    netoVariants {
      id
      sku
      defaultModel
      retailPrice
      rrp
      saveUpTo
      currency
      promotionStartDate
      promotionPrice
      promotionExpiryDate
    }
    brand {
      name
      slug
      id
      logo {
        alt
        url
      }
      model {
        apiKey
      }
      __typename
    }
    price {
      name
      slug
      id
      model {
        apiKey
      }
      __typename
    }
    usage {
      name
      slug
      id
      model {
        apiKey
      }
      __typename
    }
    size {
      name
      slug
      id
      model {
        apiKey
      }
      __typename
    }
  }

  fragment SpecialProductCard on NetoSpecial {
    name
    brand
    description
    condition
    clearanceNote
    clearancePscOffer
    clearanceUrl
    clearanceTag
    categories
    dimensions
    id
    serial: sku
    seats
    saveUpTo
    rrp
    retailPrice
    promotionStartDate
    promotionPrice
    promotionExpiryDate
    specifics {
      name
      value
    }
    warehouseData {
      WarehouseState
      WarehousePostcode
    }
  }

  fragment ClearanceProductCard on NetoSpecial {
    name
    brand
    description
    condition
    categories
    dimensions
    clearanceNote
    clearancePscOffer
    clearanceUrl
    clearanceTag
    id
    serial: sku
    seats
    saveUpTo
    rrp
    retailPrice
    promotionStartDate
    promotionPrice
    promotionExpiryDate
    specifics {
      name
      value
    }
    warehouseData {
      WarehouseState
      WarehousePostcode
    }
  }
`
